<template>
  <div>
    <b-card>
      <b-card-header class="pb-1">
        <b-card-title>{{ isEdit ? $t('services.edit') : $t('services.create') }}</b-card-title>
      </b-card-header>

      <b-card-body>
        <b-form @submit.prevent="handleSubmit">
          <b-row>
            <!-- Name -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                :label="$t('services.fields.name') + ' *'"
                label-for="name"
              >
                <b-form-input
                  id="name"
                  v-model="form.name"
                  :placeholder="$t('services.placeholders.name')"
                  :state="errors.name ? false : null"
                />
                <b-form-invalid-feedback v-if="errors.name">
                  {{ errors.name[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Service Type -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                :label="$t('services.fields.service_type') + ' *'"
                label-for="service-type"
              >
                <v-select
                  v-model="form.service_type_id"
                  :options="serviceTypeOptions"
                  :reduce="option => option.value"
                  label="label"
                  :placeholder="$t('services.placeholders.service_type')"
                  :state="errors.service_type_id ? false : null"
                >
                  <template #option="option">
                    <span>{{ option.label }}</span>
                    <gl-activity-chip
                      v-if="option.activity"
                      :activity="option.activity"
                      class="ml-50"
                    />
                  </template>
                </v-select>
                <b-form-invalid-feedback
                  v-if="errors.service_type_id"
                  :state="false"
                >
                  {{ errors.service_type_id[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Description -->
            <b-col cols="12">
              <b-form-group
                :label="$t('services.fields.description')"
                label-for="description"
              >
                <b-form-textarea
                  id="description"
                  v-model="form.description"
                  :placeholder="$t('services.placeholders.description')"
                  rows="3"
                  :state="errors.description ? false : null"
                />
                <b-form-invalid-feedback v-if="errors.description">
                  {{ errors.description[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Parent Service -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                :label="$t('services.fields.parent_service')"
                label-for="parent-service"
              >
                <v-select
                  v-model="form.parent_service_id"
                  :options="parentServiceOptions"
                  :reduce="option => option.value"
                  label="label"
                  :placeholder="$t('services.placeholders.parent_service')"
                  :state="errors.parent_service_id ? false : null"
                />
                <b-form-invalid-feedback
                  v-if="errors.parent_service_id"
                  :state="false"
                >
                  {{ errors.parent_service_id[0] }}
                </b-form-invalid-feedback>
                <small class="text-muted">{{ $t('services.messages.leave_empty_for_root') }}</small>
              </b-form-group>
            </b-col>

            <!-- Priority -->
            <b-col
              cols="12"
              md="6"
            >
              <b-form-group
                :label="$t('services.fields.priority')"
                label-for="priority"
              >
                <v-select
                  v-model="form.priority"
                  :options="priorityOptions"
                  :reduce="option => option.value"
                  label="label"
                  :placeholder="$t('services.placeholders.priority')"
                  :state="errors.priority ? false : null"
                />
                <b-form-invalid-feedback
                  v-if="errors.priority"
                  :state="false"
                >
                  {{ errors.priority[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </b-col>

            <!-- Buttons -->
            <b-col cols="12">
              <b-button
                type="submit"
                variant="primary"
                :disabled="loading"
              >
                <b-spinner
                  v-if="loading"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  icon="SaveIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ isEdit ? $t('common.update') : $t('common.create') }}</span>
              </b-button>
              <b-button
                variant="outline-secondary"
                class="ml-1"
                :to="{ name: 'services-list' }"
              >
                <feather-icon
                  icon="XIcon"
                  class="mr-50"
                />
                <span class="align-middle">{{ $t('common.cancel') }}</span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-card-body>
    </b-card>

    <!-- Service Actions (Only for Edit Mode) -->
    <b-card
      v-if="isEdit && currentService"
      class="mt-2"
    >
      <b-card-header class="pb-1">
        <b-card-title>{{ $t('services.service_actions.title') }}</b-card-title>
        <b-button
          variant="primary"
          size="sm"
          :to="{ name: 'service-actions-create', query: { service_id: $route.params.id } }"
        >
          <feather-icon
            icon="PlusIcon"
            class="mr-50"
          />
          <span class="align-middle">{{ $t('services.actions.add_action') }}</span>
        </b-button>
      </b-card-header>
      <b-card-body>
        <div v-if="currentService.actions && currentService.actions.length > 0">
          <b-list-group>
            <b-list-group-item
              v-for="action in currentService.actions"
              :key="action.id"
              class="d-flex justify-content-between align-items-center"
            >
              <div>
                <strong>{{ action.name }}</strong>
                <p class="mb-0 text-muted small">
                  {{ action.description }}
                </p>
              </div>
              <div>
                <b-badge
                  :variant="getResponsibilityVariant(action.default_responsibility_type)"
                  pill
                  class="mr-1"
                >
                  {{ formatResponsibilityType(action.default_responsibility_type) }}
                </b-badge>
                <b-button
                  variant="flat-info"
                  size="sm"
                  :to="{ name: 'service-actions-edit', params: { id: action.id } }"
                >
                  <feather-icon icon="EditIcon" />
                </b-button>
              </div>
            </b-list-group-item>
          </b-list-group>
        </div>
        <div
          v-else
          class="text-center text-muted py-2"
        >
          {{ $t('services.messages.no_actions_defined') }}
        </div>
      </b-card-body>
    </b-card>

    <!-- Activity Logs Section (Only for Edit Mode) -->
    <b-card
      v-if="isEdit && hasPermission('view activity_logs')"
      class="mt-2"
    >
      <b-card-header class="pb-1">
        <b-card-title>سجل النشاطات</b-card-title>
      </b-card-header>
      <b-card-body>
        <ActivityLogs
          :model-type="'service'"
          :model-id="Number($route.params.id)"
          :show-filters="true"
          :per-page="15"
        />
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BForm,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
  BListGroup,
  BListGroupItem,
  BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapState, mapActions } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ActivityLogs from '@/components/ActivityLogs.vue'
import GlActivityChip from '@/views/modules/service-templates/service-types/GlActivityChip.vue'
import {
  formatResponsibilityType,
  getResponsibilityVariant,
} from '@/utils/responsibilityType'

export default {
  name: 'ServicesForm',
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BForm,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormInvalidFeedback,
    BButton,
    BSpinner,
    BListGroup,
    BListGroupItem,
    BBadge,
    vSelect,
    ActivityLogs,
    GlActivityChip,
  },
  data() {
    return {
      form: {
        name: '',
        description: '',
        service_type_id: null,
        parent_service_id: null,
        priority: null,
      },
      errors: {},
      loading: false,
      serviceTypeOptions: [],
      parentServiceOptions: [],
      priorityOptions: [],
    }
  },
  computed: {
    ...mapState('services', ['currentService']),
    isEdit() {
      return !!this.$route.params.id
    },
  },
  created() {
    this.initializePriorityOptions()
    this.loadOptions()
    if (this.isEdit) {
      this.loadService()
    } else if (this.$route.query.parent_id) {
      this.form.parent_service_id = parseInt(this.$route.query.parent_id, 10)
    }
  },
  methods: {
    hasPermission(permission) {
      const userPermissions = this.$store.getters['users/user_permissions']
      if (Array.isArray(userPermissions)) {
        return userPermissions.includes(permission)
      }
      return Object.values(userPermissions || {}).includes(permission)
    },
    ...mapActions('services', [
      'fetchService',
      'createService',
      'updateService',
      'fetchServices',
    ]),
    ...mapActions('serviceTypes', ['fetchActiveServiceTypes']),
    initializePriorityOptions() {
      this.priorityOptions = [
        { label: this.$t('services.priority.1_low'), value: 1 },
        { label: this.$t('services.priority.2'), value: 2 },
        { label: this.$t('services.priority.3_medium'), value: 3 },
        { label: this.$t('services.priority.4'), value: 4 },
        { label: this.$t('services.priority.5_high'), value: 5 },
      ]
    },
    async loadOptions() {
      try {
        // Load service types
        const serviceTypesResponse = await this.fetchActiveServiceTypes()
        this.serviceTypeOptions = serviceTypesResponse.data.map(st => ({
          label: st.name_ar || st.name,
          value: st.id,
          activity: st.effective_gl_activity || null,
        }))

        // Load services for parent selection
        const servicesResponse = await this.fetchServices({ all: true })
        this.parentServiceOptions = servicesResponse.data.map(s => ({
          label: s.name,
          value: s.id,
        }))
      } catch (error) {
        console.error('Failed to load options:', error)
      }
    },
    async loadService() {
      try {
        this.loading = true
        const response = await this.fetchService(this.$route.params.id)
        this.form = {
          name: response.data.name,
          description: response.data.description,
          service_type_id: response.data.service_type_id,
          parent_service_id: response.data.parent_service_id,
          priority: response.data.priority,
        }
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || this.$t('services.messages.load_error'),
          },
        })
        this.$router.push({ name: 'services-list' })
      } finally {
        this.loading = false
      }
    },
    async handleSubmit() {
      try {
        this.loading = true
        this.errors = {}

        if (this.isEdit) {
          await this.updateService({
            id: this.$route.params.id,
            data: this.form,
          })
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.success'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('services.messages.update_success'),
            },
          })
        } else {
          await this.createService(this.form)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('common.success'),
              icon: 'CheckIcon',
              variant: 'success',
              text: this.$t('services.messages.create_success'),
            },
          })
        }

        this.$router.push({ name: 'services-list' })
      } catch (error) {
        if (error.response?.status === 422) {
          this.errors = error.response.data.errors || {}
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('common.error'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error.response?.data?.message || (this.isEdit ? this.$t('services.messages.update_error') : this.$t('services.messages.create_error')),
          },
        })
      } finally {
        this.loading = false
      }
    },
    getResponsibilityVariant(type) {
      return getResponsibilityVariant(type)
    },
    formatResponsibilityType(type) {
      return formatResponsibilityType(type)
    },
  },
}
</script>
